/* Subskrypcja, from `profile-web-mockups-v2/_blocks.css` (`.seats`, `.seatbar.flame`, `.legend`)
 * and the rows `f-zakladki.html` draws under them.
 *
 * ── The bar's ACTIVE segments are the Flame gradient ────────────────────────────────────────
 * The owner's request with this screen, and the one place on it the accent appears at all: the
 * seats a trainer has actually filled. `--accent-grad-rule` is the horizontal spelling of
 * `--accent-grad` — a 8px cell cannot show a vertical sweep — and it is the same token the four tab
 * underlines in this app read.
 *
 * Pending is a HATCH in the neutral ink, not a second colour: an outstanding invitation is a seat
 * held provisionally, and the difference between it and a filled one is a texture rather than a
 * hue. Free is the empty cell. Three states, three treatments, and the legend names all three in
 * words — colour is never the only carrier here.
 *
 * ── The three lines keep their heights in every state ───────────────────────────────────────
 * The figure's row, the bar's row and the legend's are sized here, so the skeleton stands inside
 * the reserve and the answer landing changes ink and not geometry.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The BOX is `AccountScreen.module.css`'s `.accountButton`. Only the room above it is this
 * block's own. `shared/ui/stateAction.module.css` is the app's other retry box and is deliberately
 * NOT worn here: it paints `--bg-card` because every one of its consumers stands on a card, and
 * this one stands on the sheet's `--bg` canvas. */
.retry {
  margin-top: var(--space-3);
}

/* The queued change and the double-billing warning, inside „Miejsca" where frame D of the canon
 * draws them. Warn ink on a warn wash, and the words say what it is — a line whose only signal was
 * its colour would be unreadable to half the people it is for. */
.pending {
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-note);
  color: var(--warn-ink);
  background: var(--warn-soft);
  border-radius: var(--radius-sm);
}

.figureRow {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  height: var(--h-account-figure);
}

.figure {
  font-size: var(--fs-account-figure);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-client-stat);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.figureBar {
  width: var(--w-account-figure-bar);
  height: var(--fs-h2);
}

.figureCaption {
  font-size: var(--fs-foot);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The bar's own row, sized whether or not a bar is drawn in it — an unlimited plan draws none, and
 * the block under it must not move up when it does not. */
.barRow {
  display: flex;
  align-items: center;
  height: var(--h-account-seatbar-row);
}

.bar {
  display: flex;
  flex: 1;
  gap: var(--space-3px);
}

.barSkeleton {
  width: 100%;
  height: var(--h-account-seat);
}

.seat {
  flex: 1;
  height: var(--h-account-seat);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-bar);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  height: var(--h-account-legend);
  margin-top: var(--space-2-5px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.legendEntry {
  display: inline-flex;
  gap: var(--space-1-5px);
  align-items: center;
}

.legendMark {
  width: var(--sz-account-legend-mark);
  height: var(--sz-account-legend-mark);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-hair);
}

/* The two marked states, for a bar CELL and for a legend square alike.
 *
 * They are declared AFTER both base rules deliberately: all four classes carry one compound, so
 * `.legendMark` and `.seatActive` are equal specificity and the file's order is what settles them.
 * Declared before `.legendMark`, as they were on the first pass, the legend's own background won
 * and the squares beside „4 aktywne" and „1 oczekujący" rendered empty — the legend said nothing at
 * all, which is the one thing a legend cannot do. Measured on the 1512 read-back. */
.seatActive {
  background: var(--primary-rule);
  border-color: transparent;
}

.seatPending {
  background: var(--hatch-pending);
  border-color: var(--border-2);
}

/* The row that leaves for a storefront. It wears `AccountScreen`'s `.row` for its box; what is here
 * is the hover and the focus a link owes and a `<div>` does not. */
.manage {
  cursor: pointer;
}

.manage:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── The reserve a `.pending` line stands in ─────────────────────────────────────────────────
 * The ROW, not the sentence: the sentence wears `.pending` above, because an activating
 * subscription and a queued change are the same kind of line — a fact about a moment that is not
 * now, in warn ink on a warn wash, said in words as well as in colour. They take the same box for
 * the same reason, so it is declared once and worn twice.
 *
 * What is here is the reserve. ONE of them, inside „Miejsca" above the figure, drawn in every
 * state of the tab — the three sentences it can hold are all the entitlement's answer or the
 * return flow's, and neither is known while the read is open. Conditioning the row on the answer
 * moved the figure, the bar and the legend 45px the moment it landed, measured with 900ms of
 * latency injected; conditioning it the other way moves them 45px the other way for a free
 * trainer. Held open it costs one empty line and nobody moves.
 *
 * 45px is `.pending`'s own box — two 8px insets, one 11.5px line at 1.45, and the 12px that
 * separates it from what follows. Two lines at once outgrow it; that is the one arrangement this
 * does not cover, and reserving 90px for it would be a hand's width of nothing on every tab. */
.pendingRow {
  min-height: 45px;
}

/* „Sprawdź ponownie", inside the notice. It takes the LINE's ink rather than the accent: the
 * sentence it stands in is a warn wash, and `inlineTextButton`'s default ember on that ground would
 * be the one coloured mark on a block whose accent is already spent. */
.noticeRetry {
  margin-left: var(--space-2);
  text-decoration: underline;

  --inline-text-ink: var(--warn-ink);
}

/* The invoice value is a glyph and an address on one line. `.rowIcon` is `display: block`, which
 * on its own inside an inline value would drop the address to a second line; the flex row is what
 * keeps the canon's arrangement. */
.mailValue {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
}

/* The invoice address. It is a `mailto:`, which is a link that leaves the browser in a different
 * sense than a storefront does — no new tab, nothing to announce — so it wears the row's own value
 * ink with an underline and nothing else. */
.mail {
  color: var(--text);
  text-decoration: underline;
  text-underline-offset: var(--space-2px);
}

.mail:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
